import React from 'react';
import {
  Box,
  CardContent,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Client from '../Client';
import Config from '../ClientConfig';
import BackofficeReportsApi from './backofficeReportsApi';

function thousandsSeparator(x) {
  const num = Number(x).toFixed(2);
  const parts = num.split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}

const objectToSafeValue = value => {
  if (value === null || value === undefined) return '';
  return value;
};

class SalesLastInvoices extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client('none'),
      saleslocation_list: [],
      page: 0,
      rowsPerPage: -1,
      sx_hidden: { display: 'none' },
      value: '1',
      filterData: {}
    };

    this.getData = this.getData.bind(this);
    this.doSync = this.doSync.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
  }

  componentDidMount() {
    this.getData();
  }

  getData() {
    let queryData = {};

    let data = BackofficeReportsApi.getOrdersInvoices(this.state.client, queryData);

    if (data) {
      this.setState({ saleslocation_list: data });
    }

    this.setState({ filterData: queryData });
  }

  doSync() {
    this.getData();
  }

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  };

  handleChangeRowsPerPage = event => {
    this.setState({ rowsPerPage: +event.target.value });
    this.setState({ page: 0 });
  };

  toAbsoluteUrl = file => {
    if (!file) return null;

    if (
      file.startsWith('http://') ||
      file.startsWith('https://') ||
      file.startsWith('/')
    ) {
      return file;
    }

    const base = Config.BASE_API_URL.replace(/\/+$/, '');
    return base ? `${base}/${file}` : `/${file}`;
  };

  render() {
    let page = this.state.page;
    let rowsPerPage = this.state.rowsPerPage;
    let locationSalesList = [];

    if (this.state.saleslocation_list) {
      if (this.state.saleslocation_list.result) {
        let counter = 0;

        Object.keys(this.state.saleslocation_list.result).forEach(key => {
          let fData = this.state.saleslocation_list.result[key];

          let p = {
            id: counter + 1,
            location: objectToSafeValue(fData.location),
            date: objectToSafeValue(fData.documentDate),
            cuid: objectToSafeValue(fData.cuid),
            name: objectToSafeValue(fData.name),
            serial: objectToSafeValue(fData.serial),
            sync: objectToSafeValue(fData.syncSocrate),
            value: thousandsSeparator(fData.amount || 0),
            invoiceFile: fData.file
          };

          counter += 1;
          locationSalesList.push(p);
        });
      }
    }

    const bodyCellSx = {
      py: {
        xs: 0.55,
        sm: 0.75,
        md: 0.8
      },
      px: {
        xs: 0.35,
        sm: 0.65,
        md: 1
      },
      whiteSpace: {
        xs: 'nowrap',
        md: 'normal'
      },
      color: '#111827',
      fontWeight: 450,
      overflow: {
        xs: 'hidden',
        md: 'visible'
      },
      textOverflow: {
        xs: 'ellipsis',
        md: 'clip'
      },
      wordBreak: {
        xs: 'normal',
        md: 'break-word'
      },
      lineHeight: 1.25,
      fontVariantNumeric: 'tabular-nums'
    };

    const headerCellSx = {
      py: {
        xs: 0.5,
        sm: 0.7,
        md: 0.85
      },
      px: {
        xs: 0.35,
        sm: 0.65,
        md: 1
      },
      bgcolor: '#f8fafc',
      color: '#1a237e',
      fontWeight: 650,
      lineHeight: 1.15,
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'clip'
    };

    return (
      <CardContent
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          maxHeight: '100%',
          minHeight: 0,
          p: '0 !important',
          borderRadius: {
            xs: '14px',
            sm: '16px'
          },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff'
        }}
      >
        <Box
          sx={{
            px: {
              xs: 0.75,
              sm: 1,
              md: 1.25
            },
            py: {
              xs: 0.65,
              sm: 0.75
            },
            flexShrink: 0,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)'
          }}
        >
          <Typography
            sx={{
              color: '#1a237e',
              fontWeight: 800,
              fontSize: {
                xs: '0.9rem',
                sm: '1rem',
                md: '1.05rem'
              },
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            Last Invoices
          </Typography>

          <Typography
            sx={{
              mt: 0.15,
              color: '#64748b',
              fontWeight: 600,
              fontSize: {
                xs: '0.72rem',
                sm: '0.78rem'
              },
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            Recently generated invoices and PDF download status
          </Typography>
        </Box>

        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            overflowX: 'auto',
            overflowY: 'auto',
            width: '100%',
            '& .MuiTableCell-root': {
              borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
              fontSize: {
                xs: '0.76rem',
                sm: '0.86rem',
                md: '0.92rem'
              }
            },
            '& .MuiTableRow-hover:hover': {
              backgroundColor: 'rgba(26, 35, 126, 0.035)'
            }
          }}
        >
          <Table
            stickyHeader
            size="small"
            aria-label="last invoices table"
            sx={{
              minWidth: {
                xs: 820,
                sm: 960,
                md: '100%'
              },
              width: '100%',
              tableLayout: {
                xs: 'fixed',
                md: 'auto'
              }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    ...headerCellSx,
                    position: 'sticky',
                    left: 0,
                    zIndex: 4,
                    width: {
                      xs: 58,
                      sm: 70,
                      md: '7%'
                    }
                  }}
                >
                  Sync
                </TableCell>

                {[
                  ['Location', { xs: 120, sm: 150, md: '15%' }, 'left'],
                  ['Date', { xs: 105, sm: 115, md: '11%' }, 'left'],
                  ['CUID', { xs: 88, sm: 105, md: '10%' }, 'left'],
                  ['Customer', { xs: 210, sm: 280, md: '29%' }, 'left'],
                  ['Serial', { xs: 140, sm: 170, md: '16%' }, 'left'],
                  ['Value', { xs: 88, sm: 100, md: '9%' }, 'right'],
                  ['PDF', { xs: 52, sm: 60, md: '3%' }, 'center']
                ].map(([label, width, align]) => (
                  <TableCell
                    key={label}
                    align={align}
                    sx={{
                      ...headerCellSx,
                      width
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {(rowsPerPage > 0
                ? locationSalesList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : locationSalesList
              ).map(location => {
                const href = this.toAbsoluteUrl(location.invoiceFile);

                return (
                  <TableRow hover key={location.id} tabIndex={-1}>
                    <TableCell
                      align="center"
                      sx={{
                        py: {
                          xs: 0.55,
                          sm: 0.75,
                          md: 0.8
                        },
                        px: {
                          xs: 0.35,
                          sm: 0.65,
                          md: 1
                        },
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        bgcolor: '#fff',
                        whiteSpace: 'nowrap',
                        color: '#111827',
                        fontWeight: 550,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        width: {
                          xs: 58,
                          sm: 70,
                          md: '7%'
                        }
                      }}
                    >
                      {location.sync}
                    </TableCell>

                    <TableCell align="left" sx={bodyCellSx}>
                      {location.location}
                    </TableCell>

                    <TableCell align="left" sx={bodyCellSx}>
                      {location.date}
                    </TableCell>

                    <TableCell align="left" sx={bodyCellSx}>
                      {location.cuid}
                    </TableCell>

                    <TableCell align="left" sx={bodyCellSx}>
                      {location.name}
                    </TableCell>

                    <TableCell align="left" sx={bodyCellSx}>
                      {location.serial}
                    </TableCell>

                    <TableCell align="right" sx={bodyCellSx}>
                      {location.value}
                    </TableCell>

                    <TableCell
                      align="center"
                      sx={{
                        ...bodyCellSx,
                        px: {
                          xs: 0.25,
                          sm: 0.5
                        }
                      }}
                    >
                      <Tooltip title={href ? 'Download PDF' : 'No invoice file'}>
                        <span>
                          <IconButton
                            component="a"
                            href={href || undefined}
                            target="_blank"
                            rel="noopener"
                            download
                            size="small"
                            disabled={!href}
                            aria-label="download-invoice"
                            sx={{
                              width: 30,
                              height: 30,
                              p: 0.25,
                              color: href ? '#1a237e' : 'rgba(15, 23, 42, 0.28)'
                            }}
                          >
                            <PictureAsPdfIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          labelRowsPerPage="Rows:"
          component="div"
          count={locationSalesList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={this.handleChangePage}
          onRowsPerPageChange={this.handleChangeRowsPerPage}
          sx={{
            flexShrink: 0,
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: '#fff',
            minHeight: {
              xs: 44,
              sm: 52
            },
            overflow: 'hidden',
            '& .MuiTablePagination-toolbar': {
              minHeight: {
                xs: 44,
                sm: 52
              },
              px: {
                xs: 0.5,
                sm: 1
              },
              flexWrap: {
                xs: 'wrap',
                sm: 'nowrap'
              },
              gap: {
                xs: 0.25,
                sm: 0.5
              }
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: {
                xs: '0.88rem',
                sm: '0.92rem',
                md: '1rem'
              },
              m: 0
            },
            '& .MuiTablePagination-actions': {
              ml: {
                xs: 0,
                sm: 1
              }
            }
          }}
        />
      </CardContent>
    );
  }
}

export default SalesLastInvoices;
